import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type {
  AuthTenant,
  AuthUser,
  LoginInput,
  RegisterInput,
  SessionResult,
} from '@neuroedge/shared';
import { setAccessToken, setTokenRefresher } from '../api/client';
import * as authApi from '../api/auth';

interface Session {
  user: AuthUser;
  tenant: AuthTenant;
  tenantUserId: string;
  roles: string[];
  permissions: string[];
  enabledMenus: string[];
}

type AuthState =
  { status: 'loading' } | { status: 'anonymous' } | ({ status: 'authenticated' } & Session);

interface AuthContextValue {
  state: AuthState;
  user: AuthUser | null;
  tenant: AuthTenant | null;
  tenantUserId: string | null;
  permissions: string[];
  roles: string[];
  enabledMenus: string[];
  isPlatformAdmin: boolean;
  hasPermission: (key: string) => boolean;
  /** Platform-controlled tenant entitlement - separate from `hasPermission`
   * (RBAC). `undefined` menuKey always passes (used for items that are
   * purely permission-gated, e.g. Team/Billing). */
  hasMenuAccess: (menuKey: string | undefined) => boolean;
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetches the current user/tenant/membership (e.g. after verifying an email). No-op if not authenticated. */
  refreshMe: () => Promise<void>;
  /** Adopts a session returned by an endpoint other than login/register (e.g. accepting a team invite). */
  applySession: (session: SessionResult) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toSession(s: SessionResult): Session {
  return {
    user: s.user,
    tenant: s.tenant,
    tenantUserId: s.membership.tenantUserId,
    roles: s.membership.roles,
    permissions: s.membership.permissions,
    enabledMenus: s.membership.enabledMenus,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const applySession = useCallback((s: SessionResult) => {
    setAccessToken(s.tokens.accessToken);
    setState({ status: 'authenticated', ...toSession(s) });
  }, []);

  const clearSession = useCallback(() => {
    setAccessToken(null);
    setState({ status: 'anonymous' });
  }, []);

  // Silent refresh used by the API client on a 401 and on first load.
  const silentRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const { tokens } = await authApi.refresh();
      setAccessToken(tokens.accessToken);
      return true;
    } catch {
      clearSession();
      return false;
    }
  }, [clearSession]);

  // Let the API client trigger a silent refresh + retry on a 401.
  useEffect(() => {
    setTokenRefresher(silentRefresh);
    return () => setTokenRefresher(null);
  }, [silentRefresh]);

  // Bootstrap: try to resume a session from the refresh cookie.
  //
  // Guarded by a ref, not the usual `cancelled`-flag pattern, because this
  // effect has a real side effect that must not fire twice: React 18
  // StrictMode double-invokes mount effects in development only (never in a
  // production build). A `cancelled` flag only skips *applying* the second
  // invocation's result locally - it doesn't stop the second invocation's own
  // `/auth/refresh` network call from actually firing. Two concurrent
  // refresh calls race on the same (not-yet-rotated) refresh-token cookie;
  // our backend treats reusing an already-rotated refresh token as token
  // theft and revokes the whole session - reproducing as "always logged out
  // after a page reload" reliably in local dev. The ref below ensures this
  // whole bootstrap only ever runs once per mounted provider, so there is
  // only ever one refresh call in flight.
  const bootstrapStarted = useRef(false);
  useEffect(() => {
    if (bootstrapStarted.current) return;
    bootstrapStarted.current = true;
    void (async () => {
      const ok = await silentRefresh();
      if (!ok) {
        setState({ status: 'anonymous' });
        return;
      }
      try {
        const me = await authApi.getMe();
        setState({
          status: 'authenticated',
          user: me.user,
          tenant: me.tenant,
          tenantUserId: me.membership.tenantUserId,
          roles: me.membership.roles,
          permissions: me.membership.permissions,
          enabledMenus: me.membership.enabledMenus,
        });
      } catch {
        clearSession();
      }
    })();
  }, [silentRefresh, clearSession]);

  const login = useCallback(
    async (input: LoginInput) => {
      applySession(await authApi.login(input));
    },
    [applySession],
  );

  const register = useCallback(
    async (input: RegisterInput) => {
      applySession(await authApi.register(input));
    },
    [applySession],
  );

  const refreshMe = useCallback(async () => {
    if (state.status !== 'authenticated') return;
    try {
      const me = await authApi.getMe();
      setState({
        status: 'authenticated',
        user: me.user,
        tenant: me.tenant,
        tenantUserId: me.membership.tenantUserId,
        roles: me.membership.roles,
        permissions: me.membership.permissions,
        enabledMenus: me.membership.enabledMenus,
      });
    } catch {
      // best effort - leave the current state as-is
    }
  }, [state.status]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // best effort - the local session is cleared regardless
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(() => {
    const session = state.status === 'authenticated' ? state : null;
    return {
      state,
      user: session?.user ?? null,
      tenant: session?.tenant ?? null,
      tenantUserId: session?.tenantUserId ?? null,
      roles: session?.roles ?? [],
      permissions: session?.permissions ?? [],
      enabledMenus: session?.enabledMenus ?? [],
      isPlatformAdmin: session?.user.isPlatformAdmin ?? false,
      hasPermission: (key) => (session?.permissions ?? []).includes(key),
      hasMenuAccess: (menuKey) =>
        menuKey === undefined || (session?.enabledMenus ?? []).includes(menuKey),
      login,
      register,
      logout,
      refreshMe,
      applySession,
    };
  }, [state, login, register, logout, refreshMe, applySession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
